import React from 'react'

import CommissionJustificationText from '@/components/misc/ReferralESignatureConsentPdf/components/CommissionJustificationText'
import PdfSection from '@/components/misc/ReferralESignatureConsentPdf/components/PdfSection'
import SectionHeading from '@/components/misc/ReferralESignatureConsentPdf/components/SectionHeading'
import SanitizedHtml from '@/components/misc/SanitizedHtml'
import ServiceFeesTable from '@/components/misc/ServiceFeesTable/ServiceFeesTable'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import commercialAgreementService from '@/lib/services/commercialAgreementService'
import referralService from '@/lib/services/referralService'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import {
  ReferralConsentLetterVariant,
  ReferralConsentStage,
} from '@/types/misc'
import { ReferralWithESignatureDetailsItem } from '@/types/responses/referrals'

interface Props {
  referral: ReferralWithESignatureDetailsItem
  consentStage: ReferralConsentStage
  agreementType?: CommercialAgreementTypeOption // Use to override the agreement type
  isPreviewLetter: boolean
  serviceFees: ServiceFeeDetail[]
  commissionJustification?: string // Use to override the commission justification
  onEditCommissionJustification?: (commissionJustification: string) => void
  consentLetterVariant: ReferralConsentLetterVariant
}

const CommercialInterestSection: React.FC<Props> = ({
  referral,
  consentStage,
  agreementType,
  commissionJustification,
  isPreviewLetter,
  serviceFees,
  onEditCommissionJustification,
  consentLetterVariant,
}) => {
  const { fromCompany, toCompany } = referral
  const fromCompanyName = fromCompany.presentationName
  const toCompanyName = toCompany.presentationName
  const existingServiceFees = referral.eSignatureDetails.serviceFees
  const { commercialAgreement, rejectClientFeeDisbursement } =
    referral.eSignatureDetails
  const commercialAgreementType =
    agreementType || commercialAgreement?.commercialAgreementType

  const commissionJustificationText =
    commissionJustification || // Use previously inputted text if available
    referral.eSignatureDetails.commissionJustification // Otherwise, use default wording

  const shouldShowServiceFees =
    commercialAgreementService.shouldShowServiceFees(commercialAgreementType)
  const shouldShowCommissionJustification =
    !!commissionJustificationText &&
    referralService.shouldShowCommissionJustification(commercialAgreementType)

  const shouldShowIcaewCompensationNote =
    fromCompany.isRegulatedByIcaew && referral.era

  function getServiceFees(): ServiceFeeDetail[] {
    if (serviceFees.length > 0) {
      return serviceFees
    }

    // If there are existing service fees, use them
    if (existingServiceFees.length > 0) {
      return existingServiceFees.map((serviceFee) => ({
        ...serviceFee,
        duration: serviceFee.duration?.toString(),
      }))
    }

    return []
  }

  return (
    <>
      {shouldShowServiceFees && (
        <PdfSection>
          <SectionHeading>Commercial interest</SectionHeading>

          {referral.era ? null : (
            <>
              <p>
                We may receive an introductory fee. The following are examples
                of likely fees that may be received by us and the likely
                amounts. These are examples only and may not cover all receipts
                in the future:
              </p>

              <div className="mb-2">
                <ServiceFeesTable
                  agreementType={commercialAgreementType}
                  serviceFees={getServiceFees()}
                  isPreviewLetter={isPreviewLetter}
                  consentLetterVariant={consentLetterVariant}
                  referralConsentStage={consentStage}
                />
              </div>

              <p>
                If in future we receive more than double the amount referenced
                in the above examples, we will obtain updated consent to retain
                these funds.
              </p>
            </>
          )}
        </PdfSection>
      )}

      {shouldShowCommissionJustification && (
        <CommissionJustificationText
          text={commissionJustificationText}
          onEdit={onEditCommissionJustification}
        />
      )}

      {shouldShowServiceFees && (
        <PdfSection>
          {referral.era ? (
            <p>
              We may receive an introductory fee. We will inform you when any
              fee is due to be received by us and agree with you how this is to
              be dealt with at that time.
              {!rejectClientFeeDisbursement &&
                ' Please note: you have the right to require us to remit any fees to you.'}
            </p>
          ) : (
            <p>
              You consent to us retaining such amounts and we shall not be
              required to account to you for them.
            </p>
          )}
        </PdfSection>
      )}

      <PdfSection>
        {commercialAgreementService.isRqPartnershipAgreement(
          commercialAgreementType,
        ) && (
          <>
            <SectionHeading>Commercial interest</SectionHeading>

            <p>
              We do not receive any income from {toCompany.presentationName} but
              benefit from the profit made by that firm.
            </p>
          </>
        )}

        {commercialAgreementService.isJointVentureAgreement(
          commercialAgreementType,
        ) && (
          <>
            <SectionHeading>Commercial interest</SectionHeading>

            {commercialAgreement?.jointVentureDisclaimerTextOverride ? (
              <SanitizedHtml
                className="mb-2.5 [&_p]:mb-2.5!"
                html={commercialAgreement.jointVentureDisclaimerTextOverride}
              />
            ) : (
              <p>
                {fromCompanyName} has a commercial interest in {toCompanyName}{' '}
                in the form of a joint venture and whilst we do not receive any
                income from {toCompanyName}, {fromCompanyName} does share in the
                profits made by {toCompanyName}.
              </p>
            )}
          </>
        )}

        {shouldShowIcaewCompensationNote && (
          <p>
            In the unlikely event that we cannot meet our liabilities to you,
            you may be able to claim compensation under the Chartered
            Accountants’ Compensation Scheme in respect of exempt regulated
            activities undertaken. Further information about the scheme and the
            circumstances in which grants may be made is available on ICAEW's
            website:{' '}
            <a href="https://icaew.com/cacs" target="_blank" rel="noreferrer">
              https://icaew.com/cacs
            </a>
            .
          </p>
        )}
      </PdfSection>
    </>
  )
}

export default CommercialInterestSection
